import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import type { Ref } from 'vue';
import api from '@/api';

// Define interfaces for the data structures
interface RelationshipType {
  id: string;
  slug: string;
  entity_types: string[];
  [key: string]: any; // Allow other properties
}

interface ConfigRelationshipType {
  id: string;
  slug: string;
  entity_types: string[];
  [key: string]: any; // Allow other properties
}

function getDetailFromError(err: any, fallbackMessage: string): string {
  if (err && err.message) {
    try {
      const jsonString = err.message.substring(err.message.indexOf('{'));
      const errorObject = JSON.parse(jsonString);
      if (errorObject.detail) {
        return errorObject.detail;
      }
    } catch (parseError) {
      console.error('Failed to parse error message JSON:', parseError);
    }
  }
  return fallbackMessage;
}

export const useRelationshipTypesStore = defineStore('relationshipTypes', () => {
  const relationshipTypes: Ref<RelationshipType[]> = ref([]);
  const configRelationshipTypes: Ref<ConfigRelationshipType[]> = ref([]);
  const isLoading: Ref<boolean> = ref(false);
  const error: Ref<string | null> = ref(null);

  // Only the account's owned relationship types are selectable on a policy, not pre-defined types. 
  const relationshipTypesForSelect = computed(() => {
    return relationshipTypes.value
      .filter(rt => rt.active)
  });

  function clearError() {
    error.value = null;
  }

  async function fetchRelationshipTypes() {
    // Fetch the account's owned relationship types
    isLoading.value = true;
    error.value = null;
    try {
      const response: RelationshipType[] = await api.relationshipTypes.getRelationshipTypes();
      relationshipTypes.value = response;
    } catch (err) {
      error.value = 'Failed to fetch relationship types';
      console.error(err);
    } finally {
      isLoading.value = false;
    }
  }

  // Account-admin read of the *adoptable* predefined types (active + not yet adopted) for the form.
  async function fetchConfigRelationshipTypes() {
    isLoading.value = true;
    error.value = null;
    try {
      const response: ConfigRelationshipType[] = await api.relationshipTypes.getConfigRelationshipTypes();
      configRelationshipTypes.value = response;
    } catch (err) {
      error.value = 'Failed to fetch relationship type configurations';
      console.error(err);
    } finally {
      isLoading.value = false;
    }
  }

  // System-admin read of the predefined catalog for the global management screen. A pure system
  // admin has no account context, so the account-admin endpoint above would 403 for them.
  async function fetchSystemConfigRelationshipTypes() {
    isLoading.value = true;
    error.value = null;
    try {
      const response: ConfigRelationshipType[] = await api.relationshipTypes.getSystemConfigRelationshipTypes();
      configRelationshipTypes.value = response;
    } catch (err) {
      error.value = 'Failed to fetch relationship type configurations';
      console.error(err);
    } finally {
      isLoading.value = false;
    }
  }

  async function addRelationshipType(newRelationshipType: Omit<RelationshipType, 'id'>): Promise<boolean> {
    isLoading.value = true;
    error.value = null;
    try {
      const response: RelationshipType = await api.relationshipTypes.createRelationshipType(newRelationshipType);
      relationshipTypes.value.push(response);
      return true;
    } catch (err: any) {
      error.value = getDetailFromError(err, 'An unexpected error occurred while creating the relationship type.');
      console.error(err);
      return false;
    } finally {
      isLoading.value = false;
    }
  }

  async function updateRelationshipType(slug: string, updates: Partial<RelationshipType>): Promise<boolean> {
    isLoading.value = true;
    error.value = null;
    try {
      const response: RelationshipType = await api.relationshipTypes.updateRelationshipType(slug, updates);
      const index = relationshipTypes.value.findIndex((rt) => rt.id === response.id);
      if (index !== -1) {
        relationshipTypes.value[index] = response;
      }
      return true;
    } catch (err: any) {
      error.value = getDetailFromError(err, 'An unexpected error occurred while updating the relationship type.');
      console.error(err);
      return false;
    } finally {
      isLoading.value = false;
    }
  }

  async function updateConfigRelationshipType(slug: string, updates: Partial<ConfigRelationshipType>): Promise<boolean> {
    isLoading.value = true;
    error.value = null;
    try {
      const response: ConfigRelationshipType = await api.relationshipTypes.updateConfigRelationshipType(slug, updates);
      const index = configRelationshipTypes.value.findIndex((rt) => rt.id === response.id);
      if (index !== -1) {
        configRelationshipTypes.value[index] = response;
      }
      return true;
    } catch (err: any) {
      error.value = err.message;
      console.error(err);
      return false;
    } finally {
      isLoading.value = false;
    }
  }

  async function deleteRelationshipType(id: string): Promise<void> {
    isLoading.value = true;
    error.value = null;
    try {
      await api.relationshipTypes.deleteRelationshipType(id);
      relationshipTypes.value = relationshipTypes.value.filter((rt) => rt.id !== id);
    } catch (err) {
      error.value = 'Failed to delete relationship type';
      console.error(err);
    } finally {
      isLoading.value = false;
    }
  }

  return {
    relationshipTypes,
    configRelationshipTypes,
    isLoading,
    error,
    relationshipTypesForSelect,
    clearError,
    fetchRelationshipTypes,
    fetchConfigRelationshipTypes,
    fetchSystemConfigRelationshipTypes,
    addRelationshipType,
    updateRelationshipType,
    updateConfigRelationshipType,
    deleteRelationshipType
  };
});
